<script
  setup
  lang="ts"
  generic="V extends string = string, S extends string = string"
>
import { computed, ref } from 'vue';
import {
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'reka-ui';
import { FloatingAlign } from './menu.constants';
import MenuItemRow from './menu-item.vue';
import MenuRadioItemRow from './menu-radio-item.vue';
import type { MenuEntry, MenuItem } from './menu.types';

type Props = {
  /** Rows, labels and separators, in display order. */
  items: MenuEntry<V, S>[];
  /** Which edge of the trigger the panel lines up with. */
  align?: FloatingAlign;
  /**
   * Leaves focus where it is when the menu closes instead of moving it back to
   * the trigger, for items that move focus somewhere else themselves, such as
   * back into a text editor. The menu then also stops trapping focus, so the
   * item can move it out.
   */
  skipFocusReturn?: boolean;
  /** Makes the panel exactly as wide as its trigger, e.g. under a full-width button. */
  matchTriggerWidth?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  align: FloatingAlign.START,
});

defineSlots<
  {
    /** The element that opens the menu. */
    trigger?: () => unknown;
  } & Partial<Record<S, (props: { item: MenuItem<V, S> }) => unknown>>
>();

const open = defineModel<boolean>('open', { default: false });

/** The selected value when the items are options of a value picker. */
const model = defineModel<NoInfer<V>>();

const isItem = (entry: MenuEntry<V, S>): entry is MenuItem<V, S> =>
  entry.type === undefined || entry.type === 'item';

const isOption = (
  entry: MenuEntry<V, S>,
): entry is MenuItem<V, S> & { value: V } =>
  isItem(entry) && entry.value !== undefined;

const isPicker = computed(() => props.items.some(isOption));

const groupProps = computed(() =>
  isPicker.value
    ? {
        modelValue: model.value,
        'onUpdate:modelValue': (value: V) => {
          model.value = value;
        },
      }
    : {},
);

const input = ref<'keyboard' | 'pointer'>('pointer');

const onCloseAutoFocus = (event: Event) => {
  if (props.skipFocusReturn) {
    event.preventDefault();
  }
};
</script>

<template>
  <DropdownMenuRoot v-model:open="open" :modal="!props.skipFocusReturn">
    <DropdownMenuTrigger
      as-child
      @keydown="input = 'keyboard'"
      @pointerdown="input = 'pointer'"
    >
      <slot name="trigger" />
    </DropdownMenuTrigger>
    <DropdownMenuPortal>
      <DropdownMenuContent
        :align="props.align"
        :side-offset="4"
        :collision-padding="8"
        loop
        :class="[
          'group/menu z-[10000] pointer-events-auto rounded-md border border-line bg-surface-container text-on-surface-primary shadow-[0_10px_14px_rgba(0,0,0,0.08)] outline-none p-sm',
          props.matchTriggerWidth
            ? 'w-(--reka-dropdown-menu-trigger-width)'
            : 'min-w-[12.5rem] max-w-[min(20rem,var(--reka-dropdown-menu-content-available-width))]',
        ]"
        :data-input="input"
        @close-auto-focus="onCloseAutoFocus"
        @keydown="input = 'keyboard'"
        @pointermove="input = 'pointer'"
      >
        <component
          :is="isPicker ? DropdownMenuRadioGroup : DropdownMenuGroup"
          v-bind="groupProps"
        >
          <template v-for="(entry, index) in props.items" :key="index">
            <DropdownMenuLabel
              v-if="entry.type === 'label'"
              class="px-lg pt-md pb-sm text-caption text-on-surface-secondary"
            >
              {{ entry.label }}
            </DropdownMenuLabel>
            <DropdownMenuSeparator
              v-else-if="entry.type === 'separator'"
              class="my-sm h-px bg-line"
            />
            <MenuRadioItemRow v-else-if="isOption(entry)" :item="entry">
              <template v-if="entry.slot" #default>
                <slot :name="entry.slot" :item="entry" />
              </template>
            </MenuRadioItemRow>
            <MenuItemRow v-else-if="isItem(entry)" :item="entry">
              <template v-if="entry.slot" #default>
                <slot :name="entry.slot" :item="entry" />
              </template>
            </MenuItemRow>
          </template>
        </component>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
